import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import Footer from '../components/Footer';
import SharePrizeModal from '../components/SharePrizeModal';
import { Prize } from '../types/prize';
import { getUserProfile, getUserIconPath } from '../services/userService';
import '../styles/PrizeDetailsPage.css';

const PrizeDetailsPage: React.FC = () => {
  const { prizeId } = useParams<{ prizeId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [prize, setPrize] = useState<Prize | null>(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(30);
  const [userName, setUserName] = useState<string>('');
  const [userIconPath, setUserIconPath] = useState<string>('/images/User_icon.png');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  
  // Prize expiration countdown - starts at 2 days, 6 hours, 42 min, 43 sec
  const [prizeCountdown, setPrizeCountdown] = useState({
    days: 2,
    hours: 6,
    minutes: 42,
    seconds: 43
  });

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    // Get the user's first name and icon
    if (currentUser.displayName) {
      const firstName = currentUser.displayName.split(' ')[0];
      setUserName(firstName);
    }

    // Fetch user profile for icon
    const fetchUserIcon = async () => {
      try {
        const profile = await getUserProfile(currentUser.uid);
        if (profile) {
          setUserIconPath(getUserIconPath(profile.userIcon));
        }
      } catch (error) {
        console.error('Error fetching user icon:', error);
      }
    };

    fetchUserIcon();

    const fetchPrize = async () => {
      if (!prizeId) {
        setLoading(false);
        return;
      }

      try {
        const prizeDoc = await getDoc(doc(db, 'prizes', prizeId));
        if (prizeDoc.exists()) {
          const data = prizeDoc.data();
          // Verify this prize belongs to the current user
          if (data.userId === currentUser.uid) {
            // If prize is already redeemed, redirect to main page
            if (data.isRedeemed) {
              navigate('/main');
              return;
            }
            setPrize({
              id: prizeDoc.id,
              userId: data.userId,
              campaignId: data.campaignId,
              brandName: data.brandName,
              brandLogo: data.brandLogo,
              prizeTitle: data.prizeTitle,
              prizeValue: data.prizeValue,
              category: data.category,
              claimedAt: data.claimedAt,
              expiresAt: data.expiresAt,
              isRedeemed: data.isRedeemed || false,
              redeemedAt: data.redeemedAt,
              qrCode: data.qrCode,
              beenShared: data.beenShared || false,
              sharedTo: data.sharedTo || [],
              sharedToUsernames: data.sharedToUsernames || [],
              senderId: data.senderId,
              originalWinnerId: data.originalWinnerId,
              sharedAt: data.sharedAt,
              giftMessage: data.giftMessage
            });
          }
        }
      } catch (error) {
        console.error('Error fetching prize:', error);
      }
      setLoading(false);
    };

    fetchPrize();
  }, [prizeId, currentUser, navigate]);

  // Countdown timer for QR refresh
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          // Refresh QR code
          return 30;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Prize expiration countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setPrizeCountdown(prev => {
        let { days, hours, minutes, seconds } = prev;
        
        // Countdown logic
        if (seconds > 0) {
          seconds--;
        } else if (minutes > 0) {
          minutes--;
          seconds = 59;
        } else if (hours > 0) {
          hours--;
          minutes = 59;
          seconds = 59;
        } else if (days > 0) {
          days--;
          hours = 23;
          minutes = 59;
          seconds = 59;
        } else {
          // Reset to initial time when countdown reaches 0
          return {
            days: 2,
            hours: 6,
            minutes: 42,
            seconds: 43
          };
        }
        
        return { days, hours, minutes, seconds };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleBack = () => {
    navigate('/wallet');
  };

  const handleProfileClick = () => {
    navigate('/profil');
  };

  const handleLogoClick = () => {
    if (currentUser) {
      navigate('/main');
    }
  };

  const handleRedeemClick = () => {
    setShowConfirmModal(true);
  };

  const handleCancelRedeem = () => {
    setShowConfirmModal(false);
  };

  const handleConfirmRedeem = async () => {
    if (!prizeId || isRedeeming || !currentUser) return;
    
    setIsRedeeming(true);
    
    try {
      // Update prize document
      await updateDoc(doc(db, 'prizes', prizeId), {
        isRedeemed: true,
        redeemedAt: new Date()
      });
      
      // Increment user's claimed prizes counter
      await updateDoc(doc(db, 'users', currentUser.uid), {
        antal_brugte_praemier: increment(1)
      });
      
      // Close modal and show success screen
      setShowConfirmModal(false);
      setShowSuccessScreen(true);
      
      // Redirect to wallet after 2 seconds
      setTimeout(() => {
        navigate('/wallet');
      }, 2000);
    } catch (error) {
      console.error('Error redeeming prize:', error);
      setIsRedeeming(false);
    }
  };

  const handleShareSuccess = () => {
    // Reload the prize to reflect the sharing status
    if (prizeId && currentUser) {
      getDoc(doc(db, 'prizes', prizeId)).then((prizeDoc) => {
        if (prizeDoc.exists()) {
          const data = prizeDoc.data();
          setPrize({
            id: prizeDoc.id,
            userId: data.userId,
            campaignId: data.campaignId,
            brandName: data.brandName,
            brandLogo: data.brandLogo,
            prizeTitle: data.prizeTitle,
            prizeValue: data.prizeValue,
            category: data.category,
            claimedAt: data.claimedAt,
            expiresAt: data.expiresAt,
            isRedeemed: data.isRedeemed || false,
            redeemedAt: data.redeemedAt,
            qrCode: data.qrCode,
            beenShared: data.beenShared || false,
            sharedTo: data.sharedTo || [],
            sharedToUsernames: data.sharedToUsernames || [],
            senderId: data.senderId,
            originalWinnerId: data.originalWinnerId,
            sharedAt: data.sharedAt,
            giftMessage: data.giftMessage
          });
        }
      });
    }
  };

  if (loading) {
    return (
      <div className="prize-details-page">
        <div className="prize-loading">Indlæser præmie...</div>
      </div>
    );
  }

  if (!prize) {
    return (
      <div className="prize-details-page">
        <div className="prize-error">
          <h2>Præmie ikke fundet</h2>
          <button onClick={handleBack} className="prize-back-button">
            Tilbage til Wallet
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="prize-details-page">
      {/* Main Header */}
      <div className="main-header">
        <div className="main-logo-container" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
          <img src="/images/logo.png" alt="Wiin Logo" className="main-logo" />
        </div>
        <div className="main-profile-info">
          <div className="main-greeting-container">
            <h2 className="main-greeting">Hej {userName}!</h2>
            <img 
              src={userIconPath} 
              alt="User Avatar" 
              className="main-avatar" 
              onClick={handleProfileClick}
            />
          </div>
          <p className="main-profile-link" onClick={handleProfileClick}>Din profil <span className="main-arrow">➔</span></p>
        </div>
      </div>

      {/* Header */}
      <div className="prize-header">
        <button onClick={handleBack} className="prize-back-btn">
          ← Tilbage
        </button>
        <h1 className="prize-header-title">Præmie Detaljer</h1>
        <div className="prize-header-spacer"></div>
      </div>

      {/* Prize Info */}
      <div className="prize-info-card">
        <img 
          src={prize.brandLogo} 
          alt={prize.brandName} 
          className="prize-brand-logo"
        />
        <div className="prize-title-container">
          <p className="prize-title-label">Præmienavn</p>
          <h2 className="prize-title">{prize.prizeTitle}</h2>
        </div>
        <div className="prize-value-container">
          <p className="prize-value-label">Antal</p>
          <p className="prize-value">{prize.prizeValue}</p>
        </div>
      </div>

      {/* Prize Expiration Countdown */}
      <div className="prize-expiration-countdown">
        <p className="countdown-header">Din præmie udløber om:</p>
        <p className="countdown-time">
          {prizeCountdown.days} dage, {prizeCountdown.hours} timer, {prizeCountdown.minutes} min og {prizeCountdown.seconds} sekunder
        </p>
      </div>

      {/* Instructions */}
      <div className="prize-instructions">
        <p className="prize-instruction-text">
          Vis QR-koden ved kassen for at indløse
        </p>
      </div>

      {/* QR Code Container */}
      <div className="prize-qr-container">
        <div className="prize-qr-code">
          {/* Placeholder for QR Code */}
          <div className="prize-qr-placeholder">
            <svg 
              width="200" 
              height="200" 
              viewBox="0 0 200 200" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="200" height="200" fill="white"/>
              {/* Simple QR code pattern placeholder */}
              <rect x="10" y="10" width="60" height="60" fill="black"/>
              <rect x="20" y="20" width="40" height="40" fill="white"/>
              <rect x="30" y="30" width="20" height="20" fill="black"/>
              
              <rect x="130" y="10" width="60" height="60" fill="black"/>
              <rect x="140" y="20" width="40" height="40" fill="white"/>
              <rect x="150" y="30" width="20" height="20" fill="black"/>
              
              <rect x="10" y="130" width="60" height="60" fill="black"/>
              <rect x="20" y="140" width="40" height="40" fill="white"/>
              <rect x="30" y="150" width="20" height="20" fill="black"/>
              
              {/* Random pattern */}
              <rect x="90" y="50" width="20" height="20" fill="black"/>
              <rect x="110" y="70" width="20" height="20" fill="black"/>
              <rect x="90" y="90" width="20" height="20" fill="black"/>
              <rect x="130" y="90" width="20" height="20" fill="black"/>
              <rect x="50" y="110" width="20" height="20" fill="black"/>
              <rect x="90" y="130" width="20" height="20" fill="black"/>
              <rect x="150" y="150" width="20" height="20" fill="black"/>
            </svg>
          </div>
          <p className="prize-qr-note">Generic QR Code Placeholder</p>
        </div>
        
        <div className="prize-qr-timer">
          <p className="prize-timer-text">Opdaterer om <span className="prize-countdown">{countdown}</span>s</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="prize-action-buttons">
        <button 
          className="prize-redeem-btn"
          onClick={handleRedeemClick}
          disabled={isRedeeming || prize.beenShared}
        >
          Få Præmien
        </button>
        
        {/* Share Prize Button - Only show if not redeemed and not already shared */}
        {!prize.isRedeemed && !prize.beenShared && (
          <button 
            className="prize-share-btn"
            onClick={() => setShowShareModal(true)}
          >
            Del præmie
          </button>
        )}
        
        {/* Show message if already shared */}
        {prize.beenShared && (
          <div className="prize-shared-notice">
            Denne præmie er blevet delt med: {prize.sharedToUsernames.join(', ')}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2 className="modal-title">Bekræft indløsning</h2>
            <p className="modal-text">Er du sikker på, at du vil indløse denne præmie?</p>
            <div className="modal-buttons">
              <button 
                className="modal-btn modal-confirm-btn"
                onClick={handleConfirmRedeem}
                disabled={isRedeeming}
              >
                Bekræft
              </button>
              <button 
                className="modal-btn modal-cancel-btn"
                onClick={handleCancelRedeem}
                disabled={isRedeeming}
              >
                Fortryd
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Screen */}
      {showSuccessScreen && (
        <div className="success-overlay">
          <div className="success-content">
            <h2 className="success-title">Succes!</h2>
            <img 
              src="/images/redeemed.png" 
              alt="Success" 
              className="success-checkmark"
            />
          </div>
        </div>
      )}

      {/* Share Prize Modal */}
      <SharePrizeModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        prizeId={prize.id}
        prizeName={prize.prizeTitle}
        onShareSuccess={handleShareSuccess}
      />

      {/* Footer Navigation */}
      <Footer />
    </div>
  );
};

export default PrizeDetailsPage;


